import React, { useState, useEffect } from "react";
import { Club } from "api/clubs";
import { UserProfile } from "api/user";
import { getAdminChallenges, Challenge, ChallengeMetaType } from "api/admin/challenges";
import { Action, Column, MTableAction, Query, QueryResult } from "material-table";
import { DefaultTable, HelpscoutFab } from "components";
import { Subscription } from "api/company";
import { View } from "features/global/reducer";
import { useMounted } from "utils/hooks";
import moment from "moment";
import { Box } from "@material-ui/core";
import { RemoveRedEye as DeatilsIcon } from "@material-ui/icons";
import "./challengeStyle.scss";
import { useSnackbar } from "notistack";
import ChallengeFilterDialog from "./ChallengeFilterDialog";
import ChallengeDetailDialog from "./ChallengeDetailDialog";
import CreateChallengeDialog from "./CreateChallengeDialog";
import AttentionBanner from "../admin/B2BFeaturesView/AttentionBanner";
import i18n from "./i18n";

// prop types
interface ChallengeViewProps {
    club: Club | null,
    userProfile: UserProfile | null,
    subscription: Subscription | null,
    onFetchSubscription: (clubId: string) => void,
    onNavigateTo: ({ view, tab }: { view: View, tab?: number }) => void,
}

// club-related admin stuff
const ChallengeView = ({ club, userProfile, subscription, onFetchSubscription, onNavigateTo }: ChallengeViewProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [clubId, setClubId] = useState<string | undefined>(club?.id);
    const [isLoading, setLoading] = useState(false);
    const [showChallengeFilterDialog, setShowChallengeFilterDialog] = useState(false);
    const [showCreateChallengeDialog, setShowCreateChallengeDialog] = useState<string | null>(null);
    const [showChallengeDetailDialog, setShowChallengeDetailDialog] = useState<string | null>(null);
    const [filters, setFilters] = useState<any>({});

    const userRole = userProfile?.memberships.find((m) => m.club.id === club?.id)?.userRole;

    // convert material-table query to API params
    const convertQueryToParams = (query: any) => {
        // paging
        const params: Parameters<typeof getAdminChallenges>[0] = {
            clubId,
            limit: query.pageSize,
            offset: query.pageSize * query.page,
        };
        return { ...params, ...filters };
    };
    useEffect(() => {
        setClubId(club?.id);
        if (club?.id !== undefined) {
            onFetchSubscription(club.id);
        }
    }, [club]);

    // refresh (-> remote data)
    const tableRef = React.useRef<any>(null);
    const isMounted = useMounted();
    const refreshTable = () => {
        if (!isMounted) return; // skip on initial material-table fetch
        if (tableRef.current) tableRef.current.onQueryChange();
    };
    useEffect(refreshTable, [clubId]);

    const tableActions: Action<Challenge>[] = [
        {
            icon: "filter_list",
            isFreeAction: true,
            onClick: () => { setShowChallengeFilterDialog(true); },
        },
    ];

    const checkIsActive = (sData: Date, eDate: Date) => {
        if (eDate === null) {
            return i18n.active;
        }
        const currentDateTime = new Date();
        const startDate = new Date(sData);
        const endDate = new Date(eDate);
        if (currentDateTime >= startDate && (currentDateTime <= endDate)) {
            return i18n.active;
        }
        return i18n.inActive;
    };

    const tableQuery = (query: Query<Challenge>): Promise<QueryResult<Challenge>> => new Promise((resolve) => {
        if (clubId !== undefined) {
            const params = convertQueryToParams(query);
            getAdminChallenges(params)
                .then((response) => resolve({
                    ...response,
                    page: query.page,
                }))
                .catch((err) => {
                    if (err.type !== "companyHasNoActiveSubscription") {
                        displayError(err);
                    }
                    resolve({ data: [], page: 0, totalCount: 0 });
                });
            setLoading(false);
        }
    });

    // per row actions
    const perRowActions = [
        ({ id }: Challenge) => ({
            icon: () => <DeatilsIcon />,
            tooltip: i18n.details,
            onClick: () => { setShowChallengeDetailDialog(id); },
        }),
        ({ id, metaType }: Challenge) => (metaType === ChallengeMetaType.BLUEPRINT ? {
            icon: "add_box",
            isFreeAction: true,
            tooltip: i18n.createChallenge,
            onClick: () => { setShowCreateChallengeDialog(id); },
        } : ""),
    ];

    // table columns
    const tableColumns: Column<Challenge>[] = [
        {
            field: "challengeName",
            sorting: false,
            title: i18n.title,
            width: "20%",
        },
        {
            field: "endDate",
            sorting: false,
            render: ({ startDate, endDate }) => (checkIsActive(startDate, endDate)),
            title: i18n.isActive,
        },
        {
            field: "metaType",
            sorting: false,
            render: ({ metaType }) => (metaType === ChallengeMetaType.BLUEPRINT ? i18n.yes : i18n.no),
            title: i18n.isBlueprint,
        },
        {
            field: "startDate",
            sorting: false,
            render: ({ startDate }) => (startDate ? moment(startDate).format("DD.MM.YYYY") : ""),
            title: i18n.startDate,
        },
        {
            field: "endDate",
            sorting: false,
            render: ({ endDate }) => (endDate ? moment(endDate).format("DD.MM.YYYY") : ""),
            title: i18n.endDate,
        },
        {
            field: "sportForm",
            sorting: false,
            title: i18n.sportForm,
        },
        {
            field: "challengeRewards",
            sorting: false,
            render: ({ challengeRewards }) => (challengeRewards.length > 0 ? i18n.yes : i18n.no),
            title: i18n.hasRewards,
        },
        {
            field: "numParticipants",
            sorting: false,
            title: i18n.numParticipants,
        },
        {
            align: "left",
            cellStyle: { padding: 0 },
            render: (data) => (
                <Box style={{ display: "inline-flex" }}>
                    {perRowActions.map((action, i) => (
                        <MTableAction action={action} data={data} key={i} /> // eslint-disable-line
                    ))}
                </Box>
            ),
            sorting: false,
            title: i18n.actions,
        },
    ];
    return (
        <>
            <HelpscoutFab club={club} />
            {subscription === null && club && (
                <AttentionBanner club={club} onNavigateTo={onNavigateTo} />
            )}

            {/* Challenge table */}
            {userRole !== "standard" && (
                <DefaultTable
                    actions={tableActions}
                    columns={tableColumns}
                    data={tableQuery}
                    isLoading={isLoading}
                    options={{ search: false }}
                    tableRef={tableRef}
                    title="Challenges"
                />
            )}

            {/* Challenge filter dialogs */}
            <ChallengeFilterDialog
                filters={filters}
                show={showChallengeFilterDialog}
                onClose={() => setShowChallengeFilterDialog(false)}
                onRefreshTable={refreshTable}
                onSetFilters={setFilters}
            />

            {/* Challenge details dialogs */}
            <ChallengeDetailDialog
                challengeData={tableRef.current?.dataManager.data.find((c: Challenge) => c.id === showChallengeDetailDialog)}
                show={!!showChallengeDetailDialog}
                onClose={() => setShowChallengeDetailDialog(null)}
            />

            <CreateChallengeDialog
                show={!!showCreateChallengeDialog}
                club={club}
                onRefreshTable={refreshTable}
                challengeData={tableRef.current?.dataManager.data.find((c: Challenge) => c.id === showCreateChallengeDialog)}
                onClose={() => setShowCreateChallengeDialog(null)}
            />
        </>
    );
};

export default ChallengeView;
